// src/pages/analytics/DailySnapshotTab.tsx
//
// Daily Snapshot — one-day business summary (recipe: daily-snapshot).
// First load calls with NO date; the server picks the most recent day with
// data (usually yesterday) and echoes it back, which seeds the date input.
// Picking a date refetches for that day.
//
// The response `date` is a plain yyyy-MM-dd string — rendered as-is, never
// through new Date().

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { OrderSnapshotPanel } from './OrderSnapshotPanel';
import { CalendarDays, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { SnapshotCategoryRow, SnapshotCustomerRow, DailySnapshotResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

function exportSnapshot(data: DailySnapshotResponse, exporter: typeof exportCsv = exportCsv) {
  const catCols: CsvColumns<SnapshotCategoryRow> = [
    ['Category', (r) => r.category],
    ['Qty',      (r) => r.qty],
    ['Sales',    (r) => r.sales],
    ['Est GP',   (r) => r.estGp],
    ['GP %',     (r) => r.gpPct],
  ];
  exporter(`daily-snapshot-${data.date}-categories`, catCols, data.byCategory);
  const custCols: CsvColumns<SnapshotCustomerRow> = [
    ['Customer', (r) => r.name],
    ['Sales',    (r) => r.sales],
    ['Invoices', (r) => r.invoices],
  ];
  exporter(`daily-snapshot-${data.date}-customers`, custCols, data.topCustomers);
}

function Tile({ label, value, sub, accent = 'slate' }: {
  label: string; value: string; sub?: string;
  accent?: 'blue' | 'emerald' | 'violet' | 'amber' | 'slate';
}) {
  const tint: Record<string, string> = {
    blue:    'border-blue-500/30 bg-blue-500/5',
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    violet:  'border-violet-500/30 bg-violet-500/5',
    amber:   'border-amber-500/30 bg-amber-500/5',
    slate:   'border-slate-700/40 bg-slate-900/40',
  };
  return (
    <div className={`rounded-xl border p-4 ${tint[accent]}`}>
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold text-white tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function DailySnapshotTab() {
  // null = "no user choice yet" — first fetch omits the date and the server
  // defaults to the most recent day with data.
  const [date, setDate] = useState<string | null>(null);
  const resetFilters = () => { setDate(null); };
  const { canSee } = useFeatureAccess();

  const q = useQuery({
    queryKey: ['analyticsDailySnapshot', date],
    queryFn: () => api.getDailySnapshot(date ? { date } : {}),
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const totals = data?.totals;
  const byCategory = data?.byCategory ?? [];
  const topCustomers = data?.topCustomers ?? [];
  // Input shows the user's choice, or the server-chosen day once loaded.
  const inputDate = date ?? data?.date ?? '';

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <CalendarDays size={20} /> Daily Snapshot
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          One-day summary — sales, estimated GP, invoices, customers, and payments
          {data && <span className="text-slate-500"> · {data.date}</span>}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <DateField label="Date" value={inputDate} onChange={(v) => { if (v) setDate(v); }} />
        <div className="flex-1" />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => data && exportSnapshot(data)}
          disabled={!data || (byCategory.length === 0 && topCustomers.length === 0)}
          title="Export the category + customer tables to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => data && exportSnapshot(data, exportXlsx)}
          disabled={!data || (byCategory.length === 0 && topCustomers.length === 0)}
          title="Export the category + customer tables to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the daily snapshot</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <>
          {/* KPI tiles */}
          {totals && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Tile label="Sales" value={fmtCur(totals.sales)} accent="blue" />
              <Tile label="Est GP" value={fmtCur(totals.estGp)} sub={`${totals.gpPct.toFixed(1)}% GP`} accent="emerald" />
              <Tile label="Invoices" value={fmtNum(totals.invoices)} accent="slate" />
              <Tile label="Customers" value={fmtNum(totals.customers)} accent="amber" />
              <Tile label="Payments" value={fmtCur(totals.payments)} accent="violet" />
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* By category */}
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
              <div className="px-3 py-2.5 border-b border-slate-700/40 text-sm font-semibold text-white">By category</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-700/50 bg-slate-800/50">
                      <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[140px]">Category</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Qty</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Sales</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Est GP</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">GP %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCategory.map((r, i) => (
                      <tr key={`${r.category}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                        <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[220px] truncate" title={r.category}>{r.category}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtNum(r.qty)}</td>
                        <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.sales)}</td>
                        <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.estGp)}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{r.gpPct.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {byCategory.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">No category data</div>
              )}
            </div>

            {/* Top customers */}
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
              <div className="px-3 py-2.5 border-b border-slate-700/40 text-sm font-semibold text-white">Top customers</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-700/50 bg-slate-800/50">
                      <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">Customer</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Sales</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Invoices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCustomers.map((r, i) => (
                      <tr key={`${r.name}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                        <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[260px] truncate" title={r.name}>{r.name}</td>
                        <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.sales)}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtNum(r.invoices)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {topCustomers.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">No customer data</div>
              )}
            </div>
          </div>
        </>
      )}

      {canSee('analytics.ordersnapshot') && <OrderSnapshotPanel />}
    </div>
  );
}
